// Admin-portal navigation registry — Story #14378 (Feature #14376).
//
// THE single source of truth for the admin nav (BR-NAV-006): the horizontal top-menu of AREAS,
// each area's sidebar submenu, the RBAC gates, and the route -> area resolver. Topbar.tsx and
// Sidebar.tsx both read from here; neither keeps its own nav list.
//
// Area map + placements are per the operator-approved design
// `docs/specs/tsp-xcentral/working/DESIGN-admin-nav-restructure-2026-07-29.md` and the
// signed-off mockup `.../mockups/mockup-system-prototype.html` -> `#p-nav-shell`
// (nav: Design patterns -> Admin nav shell; consolidated from the former standalone
// `admin-nav-shell-mockup.html` 2026-07-29, Task #14514) (Task #14377):
// Sign -> Clients, Work Orders -> Service Desk, Orders -> Sales, PAX8/PRISM/Reconciliation ->
// Cloud & Security, Rate Cards -> Finance, Capability Catalogue -> Automation. 8 top tabs +
// Settings behind the top-right gear.
//
// This is a nav-shell registry only — no route paths change, and it grants nothing: the API
// remains the security boundary (Constitution Article IV). Hiding an item/area is UX, and every
// gated page keeps its own in-page `useAuth` check.

// Story S1 #16040 (Epic #16035) — the hub's thirteen pages are the sidebar for this area, and they
// come from ONE registry so the nav and the routes cannot drift. `layouts/` reading a `pages/`
// module is deliberate and one-directional: XCentralHubNav.tsx is pure data with no imports of its
// own, so there is no cycle, and the alternative — restating thirteen entries here — is the drift
// this Story exists to remove.
import {
  XCENTRAL_HUB_LABEL,
  XCENTRAL_HUB_ROUTES,
  hubPath,
} from '../pages/loop-hub/XCentralHubNav';

export type AreaId =
  | 'home'
  | 'sales'
  | 'clients'
  | 'servicedesk'
  | 'cloud'
  | 'procurement'
  | 'finance'
  | 'xcentral-hub'
  | 'settings';

export interface NavItem {
  /**
   * The route this item links to. Present for every built item; omitted only when `future` is
   * set (the screen has no route yet). Enforced by a registry invariant test.
   */
  path?: string;
  label: string;
  icon: string;
  /**
   * If set, the item only shows for users holding that role. An array means "any of these".
   * Owner always passes (see `hasRequiredRole`).
   */
  requiredRole?: string | string[];
  /**
   * Not yet built — rendered as a visibly-disabled "Soon" affordance, never a link. Keeps the
   * approved area map complete (and the Procurement tab meaningful) without a dead link or a
   * stub page. Per the grill skill's In-Development placeholder posture; not a stub under
   * `_project/stub-acceptance.md` — nothing pretends to work.
   */
  future?: boolean;
  /**
   * Extra route prefixes this item owns for route -> area resolution — sibling/detail routes
   * that live outside the item's own path (e.g. Accounts owns the standalone `/m365-users/:id`
   * detail, which is only reachable from an account).
   */
  owns?: string[];
  /**
   * Match the route EXACTLY rather than by prefix when deciding whether this link is the active
   * one (#16040). Needed for an index item that sits above its own siblings — `/xcentral-hub`
   * prefix-matches all twelve of its children, so without this the Overview entry lights up on
   * every hub page and the sidebar stops telling you where you are.
   */
  exact?: boolean;
  /**
   * A short trailing chip, rendered beside the label. Distinct from `future`: a `future` item is a
   * disabled affordance with no route, whereas a chipped item IS routable — used for a page the
   * approved IA lists but the prototype deliberately does not draw (`not mocked`, #16040 AC5).
   */
  chip?: string;
}

export interface NavArea {
  id: AreaId;
  label: string;
  icon: string;
  items: NavItem[];
  /**
   * Route prefixes the area owns but does NOT surface as a sidebar item — routes reached from
   * inside a page, a button, or a redirect rather than from the nav. These are not new
   * omissions: none of them appear in the nav today either. Listed so that every built route
   * still resolves to exactly one area (BR-NAV-003).
   */
  owns?: string[];
}

/** The area a route falls back to when nothing matches, and the default landing area. */
export const DEFAULT_AREA_ID: AreaId = 'home';

/** The area reached from the top-right gear rather than a top tab. */
export const SETTINGS_AREA_ID: AreaId = 'settings';

export const AREAS: NavArea[] = [
  {
    id: 'home',
    label: 'Home',
    icon: '🧭',
    // `/help` is reached from the Topbar help button; `/my-workspace` is a legacy redirect into
    // the Hub (Wave-1 B1 #14203). Neither is a nav item today.
    //
    // `/hub/collaboration` USED to be listed here (#15300) purely as a tie-breaker: Service Desk
    // carried an Email Triage item whose path was also `/hub/collaboration`, and since areaForPath
    // takes the longest prefix and breaks ties on AREAS order (`>`, not `>=`), Home needed the
    // explicit entry to win an equal-length tie and stop the top tab flipping.
    //
    // #15301 moved that item to its own `/email-triage` route, so there is no competing prefix any
    // more and the entry became dead weight. Home still owns all three Hub tabs via its `/hub` item
    // (prefix match), which navRegistry.test.ts asserts directly — so the guarantee is unchanged,
    // it is just no longer propped up by a special case. Removed rather than left behind, because a
    // stale workaround reads as a live constraint to the next person.
    owns: ['/help', '/my-workspace'],
    items: [
      // Visible to all four internal teams per _project/role-model.md, matching HubLayout's own
      // gate (unchanged from the pre-restructure sidebar).
      {
        path: '/hub',
        label: 'Hub',
        icon: '🧭',
        requiredRole: ['Owner', 'Operations', 'Infra', 'App', 'Admin'],
      },
      { path: '/dashboard', label: 'Dashboard', icon: '📊' },
      // Story #15355 (split C1) — the staff member's OWN week, submitted as a unit. Home rather
      // than Finance deliberately: Finance's "Time Entries" is the administrative view across
      // everyone's time, whereas this is a personal surface like the Hub — and Home already owns
      // the `/my-workspace` prefix, so the route resolves here without a competing claim.
      //
      // Gated to the internal staff roles plus Contractor, matching MyTimesheetController's class
      // gate: everyone fills in a timesheet, so it is not narrowed further. Story #24593 — a
      // Contractor now logs their own time on a case and reads it back here (the time-entries
      // register stays closed to them). APPROVING one is C2's concern and has no nav entry here.
      {
        path: '/my-workspace/timesheet',
        label: 'My Timesheet',
        icon: '🗓️',
        requiredRole: ['Owner', 'Operations', 'Infra', 'App', 'Admin', 'Contractor'],
      },
    ],
  },
  {
    id: 'sales',
    label: 'Sales',
    icon: '💼',
    items: [
      { path: '/contact-leads', label: 'Contact Leads', icon: '📬' },
      // Deal Pipeline — visible to ALL internal staff: operator decision 2026-09-24 ("all team can
      // see that"), after an Infra user could create a deal but had no route to the pipeline. This
      // reverses Bug #16923. The whole of DealsController (reads AND writes, operator 2026-09-24: "we
      // need everyone to be able to do it") is AllStaff, so the rule #16923 established still holds —
      // the nav follows the API and neither is wider than the other.
      // Story #20809 — the deal detail page /deals/:dealId has no nav item of its own: it is owned
      // by this entry through segment-aware prefix matching (ownsPath), so it resolves to Sales and
      // carries the same role set as the pipeline — the page takes the same read gate as a deal.
      { path: '/deals', label: 'Deal Pipeline', icon: '💼', requiredRole: ['Owner', 'Operations', 'Infra', 'App', 'Admin'] },
      { path: '/deals/forecast', label: 'Weighted Forecast', icon: '📈', requiredRole: ['Owner', 'Operations', 'Infra', 'App', 'Admin'] },
      // Story #12194 — unified Proposals list (prospect proposals + client-facing quotes with a
      // public link), placed after Deal Pipeline / before Quotes per the plan.
      { path: '/proposals', label: 'Proposals', icon: '📄' },
      { path: '/quotes', label: 'Quotes', icon: '📝' },
      { path: '/orders', label: 'Orders', icon: '📦' },
    ],
  },
  {
    id: 'clients',
    label: 'Clients',
    icon: '🏢',
    items: [
      // Accounts owns every `/accounts/:id/...` sub-tab (contacts, departments, portal users,
      // M365 users, connected services, security posture, CSP licences) — the account is the
      // spine those pages hang off, so they keep the Clients tab active.
      { path: '/accounts', label: 'Accounts', icon: '🏢', owns: ['/m365-users'] },
      { path: '/agreements', label: 'Agreements', icon: '📋' },
      { path: '/onboarding', label: 'Onboarding', icon: '🚀' },
      { path: '/sign', label: 'Sign', icon: '✍️' },
    ],
  },
  {
    id: 'servicedesk',
    label: 'Service Desk',
    icon: '🎫',
    // `/projects/:id` (#12276) is the delivery-side detail reached from a case/quote.
    owns: ['/projects'],
    items: [
      { path: '/cases', label: 'Cases', icon: '🎫' },
      // #15300 surfaced the helpdesk@/support@ review queue in the nav; #15301 gave it its own
      // route (it previously pointed at `/hub/collaboration`, a tab inside the Home-owned Hub).
      // MUST stay SECOND: BR-NAV-001 points a top tab at its area's FIRST item, so leading with
      // this would repoint the whole Service Desk tab away from Cases (Topbar.test.tsx catches it).
      // Every internal team matches EmailReviewController's class gate (RoleAudiences.AllStaff).
      // Story #24592 added Infra (operator 2026-10-15); Story #25324 added App (operator 2026-10-21:
      // "all users need to be able to triage", internal only). Contractor is still not shown a link
      // that would 403 — hiding is UX; the API is the boundary.
      {
        path: '/email-triage',
        // Story #22980 — renamed "Support Triage" (prototype: one queue for Teams, SMS and email,
        // operator 2026-09-14). Route and second position unchanged.
        label: 'Support Triage',
        icon: '📥',
        requiredRole: ['Owner', 'Operations', 'Infra', 'App', 'Admin'],
      },
      // #18608 — the Work Orders queue, replacing the `future` placeholder IN PLACE (its position is
      // unchanged, so BR-NAV-001 still points the tab at Cases). Deliberately NO `requiredRole`: the read is
      // all staff plus Contractor (row-scoped by the API, BR-WO-001); the verbs inside are role-gated.
      { path: '/work-orders', label: 'Work Orders', icon: '🔧' },
      { path: '/escalations', label: 'Escalations', icon: '🚨' },
      // #25511 (BR-C-SRPT-001) — Case Metrics renamed Service Reports, same position. The old
      // /case-metrics route redirects here (App.tsx CaseMetricsRedirect).
      { path: '/service-reports', label: 'Service Reports', icon: '📈' },
      { path: '/resource-planning', label: 'Resource Planning', icon: '📅' },
      // Story #22980 — the approved prototype's AREAS registry orders Change requests directly after
      // Resource Planning (Cases stays first, BR-NAV-001). Client assets is not in that registry, so it
      // keeps its current predecessor (Change requests) per the #22980 grill realisation rule.
      // #18610 — the Change requests register. Deliberately NO `requiredRole`: BR-CR-001 makes the READ
      // (and raising a change) all staff; only the decision verbs inside the page are Operations-gated,
      // and the API enforces them. APPENDED, never prepended — BR-NAV-001 points the Service Desk tab at
      // this area's FIRST item, which must stay Cases.
      { path: '/change-requests', label: 'Change requests', icon: '🔀' },
      // #18611 — the Client asset register (F3.17). Deliberately NO `requiredRole`: the READ is all staff plus
      // Contractor (AllStaffWithContractor; a Contractor is read only and account-scoped by the API), and the
      // verbs inside are role-gated and rendered disabled with the reason. APPENDED, never prepended —
      // BR-NAV-001 points the Service Desk tab at this area's FIRST item, which must stay Cases.
      { path: '/service-desk/assets', label: 'Client assets', icon: '💻' },
      // #18612 — the Feedback & complaints register. Deliberately NO `requiredRole`: BR-FBK-001 is
      // "all staff read", so every internal role may open it. Only the close control inside the
      // page is Operations-gated, so no role is shown a link that would 403. APPENDED, never
      // prepended — BR-NAV-001 points the tab at its FIRST item, which must stay Cases.
      { path: '/feedback', label: 'Feedback & complaints', icon: '💬' },
      // #18613 — the cross-source alert register. Appended LAST on purpose: BR-NAV-001
      // points the Service Desk top tab at this area's FIRST item, so inserting above
      // Cases would repoint the whole tab (Topbar.test.tsx catches it). No requiredRole:
      // BR-ALR-001 makes the READ all staff, and the acknowledge/mute gate is enforced
      // inside the page and, authoritatively, at the API.
      { path: '/alerts', label: 'Central Alerting', icon: '🚨' },
      // #18614 — Monitoring. Deliberately NO `requiredRole`: BR-MON-019 makes the READ all staff,
      // so every internal role may open it, and the pause/drain verbs it does not implement live
      // on the already-built Cockpit surface. APPENDED, never prepended — BR-NAV-001 points the
      // Service Desk tab at this area's FIRST item, which must stay Cases.
      { path: '/monitoring', label: 'Monitoring', icon: '📊' },
    ],
  },
  {
    id: 'cloud',
    label: 'Cloud & Security',
    icon: '🛡️',
    // `/m365-tenants` — F11.5 foundation C (#20070): Tenant actions and Tenant history
    // (`/m365-tenants/:id/self-service-actions|invocations`) are reached from the Self-service link
    // on Account -> Connected services and from the tenant picker, never from the nav (spec rev 6),
    // so the area owns the prefix rather than listing them as items.
    owns: ['/m365-tenants'],
    items: [
      { path: '/security-posture/alerts', label: 'Security Alerts', icon: '🛡️', owns: ['/security-posture'] },
      // Story #22980 — Reconciliation before PAX8 and PRISM, per the prototype's AREAS registry.
      { path: '/reconciliation', label: 'Reconciliation', icon: '⚖️' },
      // #16194 — PAX8/PRISM supplier data. Owner/Infra/Admin, matching the API gate these pages
      // now hit: Infra owns CSP supplier routing and licences, so this is not a general staff read.
      // Hiding it from Operations/App is UX — the API is the boundary.
      { path: '/pax8-accounts', label: 'PAX8', icon: '📦', requiredRole: ['Owner', 'Infra', 'Admin'] },
      { path: '/prism-accounts', label: 'PRISM', icon: '📦', requiredRole: ['Owner', 'Infra', 'Admin'] },
      // Bug #16218 — the PAX8 spend + dormant-spend reports (#15504, #15506) were shipped and
      // RBAC-gated but had no route, no nav item and no "Soon" placeholder, so the only way to
      // read them was a bearer token and curl. APPENDED, never prepended: BR-NAV-001 points the
      // Cloud & Security tab at this area's FIRST item, which must stay Security Alerts.
      // Owner/Operations/Admin mirrors [Authorize(Roles = "Owner,Operations,Admin")] on both
      // endpoints, so Infra/App are not shown a link that would 403 — hiding is UX; the API is
      // the boundary. `/provider-spend/dormant` needs no `owns`: it sits under this item's own
      // path prefix and resolves to the area through it.
      {
        path: '/provider-spend',
        label: 'Provider Spend',
        icon: '💰',
        requiredRole: ['Owner', 'Operations', 'Admin'],
      },
      // F11.5 self-service actions — foundation C (#20070). The prototype's "Self-service" group
      // (`#p-nav-shell` cloud items, `grp:'Self-service'`): the admin sidebar has no inner group
      // headings, so the group is these two adjacent items, named so each reads on its own. Only the
      // catalogue and the MSP approval queue are nav items (spec rev 6): the approval detail is
      // reached from the queue and resolves here through the queue's path prefix. APPENDED, never
      // prepended — BR-NAV-001 lands the tab on Security Alerts.
      // Roles are spec §4's audiences exactly: catalogue read = StaffExceptApp
      // (Owner,Operations,Infra,Admin); MSP approval = InfraStaff (Owner,Infra,Admin). App sees
      // neither. Hiding is UX — the API is the boundary.
      {
        path: '/self-service-actions/catalogue',
        label: 'Action catalogue',
        icon: '⚡',
        requiredRole: ['Owner', 'Operations', 'Infra', 'Admin'],
      },
      {
        path: '/self-service-actions/pending-approvals',
        label: 'Self-service approvals',
        icon: '✅',
        requiredRole: ['Owner', 'Infra', 'Admin'],
      },
      // #11324 — Azure billing: the per-account Azure subscription register (prototype `#p-azure`).
      // APPENDED, never prepended — BR-NAV-001 lands the Cloud & Security tab on Security Alerts.
      // Owner/Operations/Infra/Admin mirrors the controller's StaffExceptApp read gate; App is not
      // shown a link that would 403. Hiding is UX — the API is the boundary.
      {
        path: '/azure',
        label: 'Azure billing',
        icon: '☁️',
        requiredRole: ['Owner', 'Operations', 'Infra', 'Admin'],
      },
      // Story #10626 (BR-TPH-013) — the Teams on-call register: tenant-scoped owner + review date per
      // assignment. APPENDED, never prepended — BR-NAV-001 lands the tab on Security Alerts.
      // Owner/Infra/Admin mirrors the controller's InfraStaff gate on every action; Operations/App are
      // not shown a link that would 403. Hiding is UX — the API is the boundary.
      {
        path: '/teams-phone/on-call',
        label: 'On-call register',
        icon: '📞',
        requiredRole: ['Owner', 'Infra', 'Admin'],
      },
      // Story #28451 (BR-TEN-D-901) — every client's M365 tenant. The prototype's 2026-10-21
      // registry puts Tenants FIRST and calls the resulting reshuffle a code follow-up; until that
      // lands it is APPENDED, so BR-NAV-001 keeps landing the tab on Security Alerts. Owns
      // `/tenants/*` so Tenant detail (#28452) stays in this area. Owner/Operations/Infra/Admin
      // mirrors the API's StaffExceptApp read gate; hiding it from App is UX — the API is the boundary.
      {
        path: '/tenants',
        label: 'Tenants',
        icon: '🏢',
        requiredRole: ['Owner', 'Operations', 'Infra', 'Admin'],
      },
    ],
  },
  {
    id: 'procurement',
    label: 'Procurement',
    icon: '🛒',
    // F3.18 (Feature #10316). The catalogue screen (C6 #13937) has landed and moves here from
    // the pre-restructure sidebar's combined "M365 / Procurement" group, per the approved area
    // map. Hardware POs held a `future: true` "Soon" placeholder until its list screen existed;
    // S6a (#10322) landed that list, so it is now a real routed entry.
    //
    // No `owns` is needed for the S6c detail (`/purchase-orders/:id`) or the S6b editor
    // (`/purchase-orders/new`, `/purchase-orders/:id/edit`) — they sit under this item's own path
    // prefix and so resolve to Procurement through it. The account-scoped create route
    // (`/accounts/:accountId/purchase-orders/new`) is deliberately NOT claimed here: it is reached
    // from an account and keeps resolving where it already did.
    items: [
      { path: '/purchase-orders', label: 'Hardware POs', icon: '🧾' },
      { path: '/hardware-catalogue', label: 'Hardware Catalogue', icon: '🖥️' },
      // Bug #16220 — the supplier registry. Appended, not inserted: BR-NAV-001 lands the top
      // tab on the area's FIRST item, so Hardware POs stays the Procurement landing page. No
      // requiredRole — the API read floor is all five internal roles; nav gating is UX, not security.
      { path: '/hardware-suppliers', label: 'Hardware Suppliers', icon: '🏭' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: '💵',
    items: [
      { path: '/invoices', label: 'Invoices', icon: '🧾' },
      // Story #28499 — the Monthly Billing Run (prototype registry: 'Billing Run') takes the Billing dashboard's
      // place; the dashboard is its Invoice status section. Owner and Admin only, as the API (BR-A-BILL-020).
      // Second, not first as in the prototype, so BR-NAV-001 keeps the Finance tab landing on Invoices.
      { path: '/billing/run', label: 'Billing Run', icon: '💵', requiredRole: ['Owner', 'Admin'] },
      // Story #24593 — gated to TimeEntriesController's read audience (AllStaff). The register lists
      // other people's time with its money; a Contractor, who may now LOG time, reads their own on
      // My Timesheet instead, so they are not offered a page that would 403.
      {
        path: '/time-entries',
        label: 'Time Entries',
        icon: '⏱️',
        requiredRole: ['Owner', 'Operations', 'Infra', 'App', 'Admin'],
      },
      // Moved out of Settings per the approved area map — rate cards are a finance artefact.
      // Story #29106 (BR-RATE-A-909) — Owner and Admin only. Operations reads only the current list New Agreement
      // uses (GET /rate-cards?current=true), so it gets no entry here. The Settings › Commercial move is module #29102.
      { path: '/rate-cards', label: 'Rate Cards', icon: '💰', requiredRole: ['Owner', 'Admin'] },
      // Story #15357 (split C3) — who has not submitted their timesheet. FINANCE rather than Home
      // deliberately: Home's 'My Timesheet' (#15355) is a personal surface, whereas this is the
      // administrative view ACROSS everyone's time — the same distinction that already puts
      // 'Time Entries' here. Appended last so BR-NAV-001 keeps the Finance tab landing on Invoices.
      //
      // Gated to Owner/Operations/Admin, matching TimesheetsController's class gate. Narrower than
      // 'My Timesheet' on purpose: everyone fills one in, but this report shows other people's
      // compliance. Infra and App are excluded deliberately (a team lead chasing their own team is a
      // future refinement, not this entry).
      {
        path: '/timesheets/not-submitted',
        label: 'Timesheet Compliance',
        icon: '📋',
        requiredRole: ['Owner', 'Operations', 'Admin'],
      },
      // Story #23644 — BR-TIME-003 (B): non-billable utilisation by staff and account, distinct from
      // project utilisation. `/time-entries/utilisation` resolves to Finance through the
      // `/time-entries` prefix. Appended last so BR-NAV-001 keeps Invoices as the Finance landing tab.
      // Gated to Owner/Operations/Admin, matching UtilisationReportController (UX only).
      {
        path: '/time-entries/utilisation',
        label: 'Utilisation',
        icon: '📊',
        requiredRole: ['Owner', 'Operations', 'Admin'],
      },
    ],
  },
  {
    // Renamed from `automation` / "Automation" by Story S1 #16040 (prototype living-sync
    // 2026-08-31): the area IS the XCentral Hub, so it carries the name and its thirteen pages are
    // its primary list. The old shape — an area called "Automation" containing a group called
    // "XCentral Hub" — was one level of naming too many and is no longer what the prototype shows.
    // The UI label and route change; the `api/v1/loop-hub/*` REST prefix does NOT (#16040 AC3).
    id: 'xcentral-hub',
    label: XCENTRAL_HUB_LABEL,
    icon: '🤖',
    // Bug #16200 — `/ai-oversight` used to be owned here, because the page had a route and no nav
    // entry. It now HAS a nav entry, in Settings (see the Settings area below), so it must NOT be
    // owned here as well. This is load-bearing, not tidying: areaForPath keeps the LONGEST owned
    // prefix and breaks an equal-length tie by AREAS order, `xcentral-hub` precedes `settings`, and
    // '/ai-oversight' as a Settings item path is exactly as long as '/ai-oversight' here — so
    // leaving this entry in would silently win the tie and keep resolving the route to this area,
    // lighting the wrong top tab under the new menu item.
    owns: ['/loop-hub'],
    // The hub pages, straight from the registry, in prototype order (#16040 AC1). Each entry's roles
    // come from the SAME `route.roles` LoopHubCockpit.tsx's route guard reads (Story #26091), so a page
    // cannot be in a role's nav and redirect it, or the reverse. Operations and Infra see the read
    // pages the prototype grants them (BR-HUB-A-944); every other page stays Owner/Admin/App.
    items: XCENTRAL_HUB_ROUTES.map(route => ({
      path: hubPath(route.slug),
      label: route.label,
      icon: route.icon,
      requiredRole: [...route.roles],
      // Overview is the index; without this it prefix-matches every sibling and stays lit.
      exact: route.slug === '',
      ...(route.chip ? { chip: route.chip } : {}),
    })),
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: '⚙️',
    // Notification preferences are reached from the notification panel, not the nav.
    // `/integrations` moved here from Cloud & Security with its nav item (Story #22980, BR-NAV-003):
    // CSP supplier routing (`/integrations/csp-routing`, #11612) and the legacy `/integrations/xero`
    // redirect (#12983) are page-reached from Integrations & services, so they resolve to the same
    // area as their parent page instead of flipping the top tab to Cloud & Security.
    owns: ['/notification-preferences', '/integrations'],
    items: [
      // Story #22980 — Settings follows the approved prototype's sequence, Audit Log last. Provider SKU
      // Mappings has no prototype position, so it keeps its predecessor (Capability Catalogue).
      { path: '/companies', label: 'Companies', icon: '🏢' },
      // #14950 — billing entity -> internal-account attribution. Owner/Operations only, matching
      // the API gate on both of its endpoints (AC6): this is billing configuration deciding which
      // company bears internal cost, so it belongs to the billing function per
      // _project/role-model.md. Hiding it from Infra/App is UX — the API is the boundary.
      {
        path: '/companies/internal-attribution',
        label: 'Internal Attribution',
        icon: '🏛️',
        requiredRole: ['Owner', 'Operations'],
      },
      { path: '/service-tiers', label: 'Service Tiers', icon: '🏷️' },
      // Moved here from the Automation/XCentral Hub area by Story S1 #16040 (prototype
      // living-sync 2026-08-31). It is REFERENCE CONFIGURATION — the capability -> required
      // service-plan map that licence-upsell resolution reads — not a delivery screen, so it does
      // not belong in an area named for the build fleet. Role gate unchanged: Owner/App/Admin,
      // mirroring CapabilitiesController.UpdateMapping. The route is unchanged too, so existing
      // links and bookmarks are unaffected.
      {
        path: '/settings/capability-catalogue',
        label: 'Capability Catalogue',
        icon: '🧩',
        requiredRole: ['Owner', 'App', 'Admin'],
      },
      // #16405 — the provider-SKU -> M365 licence map. Reference configuration that licence
      // reconciliation reads. Gated on the controller's staff READ floor, NOT its narrower
      // MutateRoles: Operations and App are authorised readers, so gating the link on
      // Owner/Infra/Admin would leave them able to read the page but unable to find it. The
      // page hides its own mutating controls for non-mutate roles; the API is the boundary.
      {
        path: '/settings/provider-sku-mappings',
        label: 'Provider SKU Mappings',
        icon: '🔗',
        requiredRole: ['Owner', 'Operations', 'Infra', 'App', 'Admin'],
      },
      // Story #22980 — moved from Cloud & Security to the Settings gear as "Integrations & services"
      // (prototype registry: setup and policy are configuration). Route and role gate unchanged.
      // #14638 — provider connection status + reconnect. Owner/Infra/Admin, matching the API gate
      // on both of its endpoints: this is an infrastructure concern, not a general staff read.
      // Hiding it from Operations/App is UX — the API is the boundary.
      {
        path: '/integrations/providers',
        label: 'Integrations & services',
        icon: '🔌',
        requiredRole: ['Owner', 'Infra', 'Admin'],
      },
      { path: '/incident-domains', label: 'Incident Config', icon: '⚙️' },
      // Bug #16254 (defect B) — nav only; both controllers are correct and unchanged.
      // TermsController gates 7 of its 8 actions `Owner,Operations,Admin` (the eighth, GET
      // /terms/active, is deliberately staff-wide reference data and is not what this page lists
      // from), and AgreementTemplatesController gates all 8 the same. Neither panel carries an RBAC
      // line in the prototype, so nothing contradicts that set. Read floor and mutate set are
      // identical here, so the nav gate equals both. Infra and App were being offered dead links.
      {
        path: '/terms',
        label: 'Terms',
        icon: '📄',
        requiredRole: ['Owner', 'Operations', 'Admin'],
      },
      {
        path: '/agreement-templates',
        label: 'Agreement Templates',
        icon: '📑',
        requiredRole: ['Owner', 'Operations', 'Admin'],
      },
      // #16194 — Sync Status reads /sync/status + /sync/log, both now Owner/Infra/Admin. MSP-internal
      // sync health is an infrastructure concern. Hiding it from Operations/App is UX — the API is
      // the boundary.
      { path: '/sync', label: 'Sync Status', icon: '🔄', requiredRole: ['Owner', 'Infra', 'Admin'] },
      // Bug #16200 — AI Oversight had a live route (#14203) and no door: no nav entry anywhere, and
      // the Cockpit's only affordance pointed at the retired /my-workspace. Placed in
      // Settings because the signed-off prototype says so — mockup-system-prototype.html
      // :1707, "AI Oversight relocates out of the inbox to a governance area" — and because Audit
      // Log is where this project already keeps its other read-only compliance record. Settings
      // also sits behind the gear rather than one of the 8 top tabs, so it is reachable by
      // Operations, who cannot see the Cockpit at all and therefore had no route in whatsoever.
      // Roles mirror AiOversightController's [Authorize(Roles = "Owner,Operations,Admin")] exactly
      // — neither wider nor narrower; the API remains the boundary.
      {
        path: '/ai-oversight',
        label: 'AI Oversight',
        icon: '🧠',
        requiredRole: ['Owner', 'Operations', 'Admin'],
      },
      { path: '/users-roles', label: 'Users & Roles', icon: '👥', requiredRole: 'Owner' },
      { path: '/contractors', label: 'Contractors', icon: '🔧', requiredRole: 'Owner' },
      // Bug #16254 (defect A) — kept LAST per Story #22980 (prototype sequence). — the link was ungated while AuditLogController gated `Owner,Admin`,
      // so Operations, Infra, App and Contractor were all offered a menu item that always 403'd and
      // read as an outage rather than a permission decision. The fix widened the CONTROLLER, because
      // the signed-off prototype's BR-A-AUD-003 grants "Owner / Operations / Infra view" — the API
      // was contradicting the captured requirement, not the other way round. This gate mirrors the
      // widened `[Authorize(Roles = "Owner,Operations,Infra,Admin")]` exactly: neither wider nor
      // narrower. Hiding is UX — the API is the boundary.
      {
        path: '/audit-log',
        label: 'Audit Log',
        icon: '🔍',
        requiredRole: ['Owner', 'Operations', 'Infra', 'Admin'],
      },
    ],
  },
];

/**
 * Does the caller hold a role that satisfies `required`? No requirement = visible to all.
 * Owner short-circuits (BR-NAV-004 "Owner sees all") — every gated item already lists Owner, so
 * this makes that guarantee structural rather than incidental.
 */
export function hasRequiredRole(roles: string[], required?: string | string[]): boolean {
  if (!required) return true;
  if (roles.includes('Owner')) return true;
  const requiredList = Array.isArray(required) ? required : [required];
  return requiredList.some(r => roles.includes(r));
}

export function areaById(id: AreaId): NavArea {
  return AREAS.find(a => a.id === id) ?? AREAS[0];
}

/** The area's items this caller may see. */
export function visibleItems(area: NavArea, roles: string[]): NavItem[] {
  return area.items.filter(item => hasRequiredRole(roles, item.requiredRole));
}

/**
 * The 8 top-menu areas this caller may see (Settings is excluded — it lives behind the gear).
 * An area is hidden when the caller can see none of its items (BR-NAV-004).
 */
export function visibleTabAreas(roles: string[]): NavArea[] {
  return AREAS.filter(
    area => area.id !== SETTINGS_AREA_ID && visibleItems(area, roles).length > 0,
  );
}

/** Whether the caller may see the Settings area (i.e. whether to show the gear). */
export function canSeeSettings(roles: string[]): boolean {
  return visibleItems(areaById(SETTINGS_AREA_ID), roles).length > 0;
}

/** Segment-aware prefix match: `/accounts` owns `/accounts/5` but not `/accounts-archive`. */
function ownsPath(prefix: string, pathname: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/**
 * Resolve a route to its area (BR-NAV-003) so deep-linking selects the right tab + submenu.
 * Longest owned prefix wins, so `/deals/forecast` beats `/deals`. Unknown routes fall back to
 * the default area rather than leaving no tab selected.
 */
export function areaForPath(pathname: string): AreaId {
  let bestId: AreaId = DEFAULT_AREA_ID;
  let bestLength = -1;

  for (const area of AREAS) {
    const prefixes: string[] = [...(area.owns ?? [])];
    for (const item of area.items) {
      if (item.path) prefixes.push(item.path);
      if (item.owns) prefixes.push(...item.owns);
    }
    for (const prefix of prefixes) {
      if (ownsPath(prefix, pathname) && prefix.length > bestLength) {
        bestId = area.id;
        bestLength = prefix.length;
      }
    }
  }

  return bestId;
}

/**
 * Routes that manage their own height and their own scrolling — Story #17760 (BR-HUB-V-127).
 *
 * `AdminLayout` renders ONE scroller for the whole app: `<main className="flex-1 overflow-y-auto
 * p-6">`. That is right for a document-shaped page and wrong for a two-pane screen: inside a
 * scrolling parent a child cannot be "the remaining height", so it grows instead, the page scrolls,
 * and the tab strip, the filter bar and the answer buttons all leave the viewport. On the Hub Inbox
 * that means scrolling past the question to reach the verbs, which is the failure BR-HUB-V-127
 * forbids.
 *
 * A route listed here gets `overflow-hidden` instead, and owns its padding and its scroll regions.
 *
 * WHY A LIST AND NOT A ROUTE `handle`: the app uses declarative `<Routes>`, not a data router, so
 * there is no `handle` to hang this on. A pathname predicate is the honest alternative — explicit,
 * unit-testable, and greppable — rather than threading a context through every page just to move a
 * CSS class.
 *
 * DEFAULT IS NORMAL, DELIBERATELY. An unrecognised path keeps today's scroller. Getting this
 * backwards would silently remove the scrollbar from every page the predicate does not know about,
 * which is a far worse failure than a full-height page that scrolls the old way.
 *
 * DERIVED FROM THE REGISTRY, not hard-coded. The Hub Inbox answers to its slug AND to two aliases
 * (`blockages`, `approvals`) which stay in bookmarks, chat links and runbooks — a hand-written list
 * would cover `/xcentral-hub/inbox` and silently miss the other two, so an operator arriving from a
 * saved link would get the old scroller and none of the layout this Story exists to enable. The
 * same registry that owns the aliases owns this.
 */
const FULL_HEIGHT_HUB_KEYS: readonly string[] = ['hub-inbox'];

const FULL_HEIGHT_PREFIXES: readonly string[] = XCENTRAL_HUB_ROUTES.filter((r) =>
  FULL_HEIGHT_HUB_KEYS.includes(r.key),
).flatMap((r) => [hubPath(r.slug), ...(r.aliases ?? []).map((a) => hubPath(a))]);

export function isFullHeightRoute(pathname: string): boolean {
  return FULL_HEIGHT_PREFIXES.some((prefix) => ownsPath(prefix, pathname));
}
